import { useCallback } from 'react';

import { useIntl } from 'react-intl';

import { getMergeQueuesUrl } from 'src/components/merge-queues/constants';
import { useFlag } from 'src/hooks/flag';
import { useMutation } from 'src/utils/fb-query';
import { authRequest, jsonHeaders } from 'src/utils/fetch';
import { captureExceptionWithTags } from 'src/utils/sentry';

import { messages } from '../merge-queues.i18n';
import { UseMergeQueueActionParams } from '../types';

const deleteMergeQueueRequest = async ({
  mergeQueueUuid,
  owner,
  slug,
}: Omit<UseMergeQueueActionParams, 'onClose' | 'onSuccess'>) => {
  const url = `${getMergeQueuesUrl(owner, slug)}/${mergeQueueUuid}`;

  const response = await fetch(
    authRequest(url, {
      method: 'DELETE',
      headers: jsonHeaders,
    })
  );

  if (!response.ok) {
    throw new Error(
      `Failed to delete merge queue: ${response.status} ${response.statusText}`
    );
  }

  // DELETE requests return empty responses, so don't try to parse JSON
  return Promise.resolve();
};

export const useDeleteMergeQueue = ({
  mergeQueueUuid,
  owner,
  slug,
  onClose,
  onSuccess,
}: UseMergeQueueActionParams) => {
  const { showSuccessFlag, showErrorFlag } = useFlag();
  const { formatMessage } = useIntl();

  const deleteMergeQueueCallback = useCallback(
    () => deleteMergeQueueRequest({ mergeQueueUuid, owner, slug }),
    [mergeQueueUuid, owner, slug]
  );

  const { mutate: deleteMergeQueue, isPending: isDeletingMergeQueue } =
    useMutation(deleteMergeQueueCallback, {
      onSuccess: () => {
        onSuccess?.();
        showSuccessFlag(formatMessage(messages.mergeQueueDeleteSuccess));
      },
      onError: (error: Error) => {
        captureExceptionWithTags(error, {
          section: 'merge-queue-configuration',
          action: 'delete-merge-queue',
        });
        showErrorFlag(formatMessage(messages.mergeQueueDeleteError));
      },
      onFinished: () => {
        onClose();
      },
    });

  return {
    deleteMergeQueue,
    isDeletingMergeQueue,
  };
};
